import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Her istekte token ekle
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 gelince token yenile, olmazsa logout
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(`${BASE_URL}/api/auth/refresh/`, { refresh });
          localStorage.setItem('access_token', data.access);
          original.headers.Authorization = `Bearer ${data.access}`;
          return client(original);
        } catch {
          // refresh da geçersiz — temizle
        }
      }
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default client;

// Kısa yollar
export const api = {
  // Auth
  login: (email, password) =>
    client.post('/api/auth/login/', { username: email, password }),

  // Kullanıcılar
  getMe: () => client.get('/api/users/me/'),
  getUsers: () => client.get('/api/users/'),
  getTeamCalendar: () => client.get('/api/team-calendar/'),

  // İzinler
  getLeaves: () => client.get('/api/leaves/'),
  createLeave: (data) => client.post('/api/leaves/', data),
  deleteLeave: (id) => client.delete(`/api/leaves/${id}/`),

  // İşler
  getTasks: (params) => client.get('/api/tasks/', { params }),
  getTask: (id) => client.get(`/api/tasks/${id}/`),
  createTask: (data) => client.post('/api/tasks/', data),
  updateTask: (id, data) => client.patch(`/api/tasks/${id}/`, data),
  completeTask: (id, data) => client.post(`/api/tasks/${id}/complete/`, data),
  generateOutput: (id, mode) =>
    client.post(`/api/tasks/${id}/generate-output/`, { mode: mode || 'short' }),
  previewTaskOutput: (description, mode) =>
    client.post('/api/tasks/preview-output/', { description, mode: mode || 'short' }),
  delegateTask: (id, data) => client.post(`/api/tasks/${id}/delegate/`, data),
  reviseDeadline: (id, data) => client.post(`/api/tasks/${id}/revise-deadline/`, data),
  requestDeleteTask: (id, data) => client.post(`/api/tasks/${id}/request-delete/`, data),
  adminDeleteTask: (id) => client.delete(`/api/tasks/${id}/admin-delete/`),
  renameTaskTitle: (id, title) => client.patch(`/api/tasks/${id}/rename-title/`, { title }),
  summarizeMail: (id, mailText) => client.post(`/api/tasks/${id}/summarize-mail/`, { mail_text: mailText }),

  // Süreç notları
  getNotes: (taskId) => client.get(`/api/tasks/${taskId}/notes/`),
  createNote: (taskId, data) => {
    const form = new FormData();
    if (data.text) form.append('text', data.text);
    if (data.audio_file) form.append('audio_file', data.audio_file);
    if (data.images) {
      data.images.slice(0, 5).forEach((img) => form.append('images', img));
    }
    if (data.files) {
      data.files.slice(0, 10).forEach((f) => form.append('files', f));
    }
    return client.post(`/api/tasks/${taskId}/notes/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Metin (ve isteğe subprocess) — multipart ile süreç notu */
  createProcessNote: (taskId, { text, subprocess } = {}) => {
    const form = new FormData();
    if (text) form.append('text', text);
    if (subprocess != null && subprocess !== '') form.append('subprocess', String(subprocess));
    return client.post(`/api/tasks/${taskId}/notes/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteNoteImage: (imageId) => client.delete(`/api/notes/images/${imageId}/`),
  deleteNoteFile: (fileId) => client.delete(`/api/notes/files/${fileId}/`),
  restoreNoteImages: (noteId) => client.post(`/api/notes/${noteId}/restore-images/`),
  updateNote: (noteId, text) => client.patch(`/api/notes/${noteId}/`, { text }),
  adminDeleteNote: (noteId) => client.delete(`/api/notes/${noteId}/admin-delete/`),

  // Kategoriler
  getCategories: () => client.get('/api/categories/'),
  requestCategory: (data) => client.post('/api/categories/request/', data),
  updateCategory: (id, data) => client.patch(`/api/categories/${id}/`, data),
  deleteCategory: (id) => client.delete(`/api/categories/${id}/`),

  // Raporlar
  getReport: (params) => client.get('/api/reports/', { params }),
  getReportPdf: (params) =>
    client.get('/api/reports/', { params: { ...params, dl: 'pdf' }, responseType: 'blob' }),

  // Müşteriler
  getCustomers: (search) => client.get('/api/customers/', { params: search ? { search } : {} }),
  getCustomer: (id) => client.get(`/api/customers/${id}/`),
  createCustomer: (data) => client.post('/api/customers/', data),
  updateCustomer: (id, data) => client.patch(`/api/customers/${id}/`, data),
  deleteCustomer: (id) => client.delete(`/api/customers/${id}/delete/`),
  checkDuplicate: (name) => client.post('/api/customers/check-duplicate/', { name }),
  getCustomerTypeOptions: () => client.get('/api/customers/type-options/'),
  createCustomerTypeRequest: (name) => client.post('/api/customers/type-requests/', { name }),
  reviewCustomerTypeRequest: (id, action, reason) => client.post(`/api/customers/type-requests/${id}/review/`, { action, reason }),
  getContacts: (customerId) => client.get(`/api/customers/${customerId}/contacts/`),
  createContact: (customerId, data) => client.post(`/api/customers/${customerId}/contacts/`, data),
  updateContact: (customerId, contactId, data) => client.patch(`/api/customers/${customerId}/contacts/${contactId}/`, data),
  deleteContact: (customerId, contactId) => client.delete(`/api/customers/${customerId}/contacts/${contactId}/`),

  // Paralel Süreçler
  getSubProcesses: (taskId) => client.get(`/api/tasks/${taskId}/subprocesses/`),
  createSubProcess: (taskId, data) => client.post(`/api/tasks/${taskId}/subprocesses/`, data),
  updateSubProcess: (taskId, id, data) => client.patch(`/api/tasks/${taskId}/subprocesses/${id}/`, data),
  deleteSubProcess: (taskId, id) => client.delete(`/api/tasks/${taskId}/subprocesses/${id}/`),

  // Geri Bildirim
  getFeedbackUnreadCount: () => client.get('/api/feedback/unread-count/'),
  getFeedbacks: () => client.get('/api/feedback/'),
  getFeedback: (id) => client.get(`/api/feedback/${id}/`),
  createFeedback: (data) => {
    const form = new FormData();
    form.append('title', data.title);
    form.append('body', data.body);
    if (data.files) data.files.slice(0, 5).forEach(f => form.append('files', f));
    return client.post('/api/feedback/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  replyFeedback: (id, data) => client.post(`/api/feedback/${id}/reply/`, data),
  updateFeedbackStatus: (id, status) => client.patch(`/api/feedback/${id}/`, { status }),
  deleteFeedbackFile: (fileId) => client.delete(`/api/feedback/files/${fileId}/`),

  // Bildirimler
  getNotifications: () => client.get('/api/notifications/'),
  markRead: (id) => client.post(`/api/notifications/${id}/read/`),
  markAllRead: () => client.post('/api/notifications/mark-all-read/'),
  approveCategory: (categoryId) => client.post(`/api/notifications/approve-category/${categoryId}/`),
  rejectCategory: (categoryId, reason) => client.post(`/api/notifications/reject-category/${categoryId}/`, { reason }),
  approveDelete: (taskId) => client.post(`/api/notifications/approve-delete/${taskId}/`),
  rejectDelete: (taskId, reason) => client.post(`/api/notifications/reject-delete/${taskId}/`, { reason }),
};
